import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAdminAuthDataOrThrow } from '../../../../auth';
import { trpc } from '../../../../trpc/react';

export type TumUser = {
  axleProfileId: string;
  givenName: string;
  familyName: string;
  currentIsCoach: boolean;
  newIsCoach: boolean;
  currentHasMobileAccess: boolean | null;
  newHasMobileAccess: boolean;
  currentHasWebAccess: boolean | null;
  newHasWebAccess: boolean;
  currentTeamNames: string[];
  newTeamNames: string[];
  currentCoachTeamNames: string[];
  newCoachTeamNames: string[];
};

/**
 * Upper bound for the load-users request. The server fetches every profile
 * from Axle, so a large org can take a while — but a request must never hang
 * the button forever (SC: stuck "Loading..." with no recovery). Generous
 * enough for slow upstreams; each click issues a fresh request.
 */
const LOAD_USERS_TIMEOUT_MS = 120_000;

/**
 * State machine for the TUM Sync admin tool page.
 *
 * Owns the three wizard selections (org, team, loaded users) and the two
 * server round trips they drive: `getTumSyncUsers` (load, with an abort
 * and a client timeout) and `runTumSync` (apply, guarded so only the
 * reviewed plan's team scope can be synced).
 *
 * The team selection defaults to the first team once the org's teams
 * arrive, so an all-teams sync — the option that times out for large
 * orgs — is never the resting state.
 */
export function useTumSync() {
  const { userId: adminUserId } = useAdminAuthDataOrThrow();
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  /**
   * The team the sync is scoped to, as chosen on the page. `undefined` means
   * no choice yet, `null` is the explicit "All teams" option, and a string
   * is the org's Team.id. The value is sent as `teamId` and the cached plan
   * refuses to apply if the scope no longer matches. The selection defaults
   * to the first team once the org's teams arrive, so the page never
   * defaults to an all-teams sync.
   */
  const [selectedTeamId, setSelectedTeamId] = useState<
    string | null | undefined
  >(undefined);
  const [users, setUsers] = useState<TumUser[]>([]);
  const [count, setCount] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [synced, setSynced] = useState(false);
  const [syncedCount, setSyncedCount] = useState(0);
  const [failureCount, setFailureCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);

  const utils = trpc.useUtils();
  // Tracks the in-flight load so a stale response (or a stale failure) never
  // clobbers state after the user moved on, and so a new attempt always
  // issues a fresh request instead of waiting on a wedged one.
  const loadAbortRef = useRef<AbortController | null>(null);

  const orgsQuery = trpc.admin.orgs.useQuery(undefined);

  const coachProfilesQuery = trpc.admin.getCoachProfiles.useQuery(
    { orgId: selectedOrgId ?? '' },
    { enabled: !!selectedOrgId },
  );

  const adminHasCoachProfile = useMemo(() => {
    if (!selectedOrgId || !coachProfilesQuery.data) return null;
    return coachProfilesQuery.data.some(
      (profile) => profile.userId === adminUserId,
    );
  }, [adminUserId, coachProfilesQuery.data, selectedOrgId]);

  const tumEnabledQuery = trpc.admin.tumSync.isTumEnabled.useQuery(
    { orgId: selectedOrgId ?? '' },
    { enabled: !!selectedOrgId },
  );

  const teamsQuery = trpc.admin.tumSync.getTeams.useQuery(
    { orgId: selectedOrgId ?? '' },
    { enabled: !!selectedOrgId },
  );

  // Default the selection to the first team once the org's teams arrive, so
  // an all-teams sync is never the resting state (it is the option that
  // times out for large orgs). A cleared select snaps back to the first
  // team rather than to all teams; choosing "All teams" sets null, which
  // this effect leaves alone.
  useEffect(() => {
    const firstTeamId = teamsQuery.data?.[0]?.id;
    if (selectedTeamId === undefined && firstTeamId) {
      setSelectedTeamId(firstTeamId);
    }
  }, [selectedTeamId, teamsQuery.data]);

  const runTumSyncMutation = trpc.admin.tumSync.runTumSync.useMutation();

  const reset = useCallback(() => {
    loadAbortRef.current?.abort();
    loadAbortRef.current = null;
    setUsers([]);
    setCount(0);
    setHasLoaded(false);
    setIsLoading(false);
    setIsSyncing(false);
    setSynced(false);
    setSyncedCount(0);
    setFailureCount(0);
    setLoadError(null);
  }, []);

  const handleOrgChange = useCallback(
    (orgId: string | null) => {
      setSelectedOrgId(orgId);
      // Teams belong to the org; the selection cannot survive the switch.
      setSelectedTeamId(undefined);
      reset();
    },
    [reset],
  );

  const handleTeamChange = useCallback(
    (teamId: string | null | undefined) => {
      setSelectedTeamId(teamId);
      reset();
    },
    [reset],
  );

  const handleLoadUsers = useCallback(async () => {
    if (!selectedOrgId || selectedTeamId === undefined) return;
    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, LOAD_USERS_TIMEOUT_MS);

    setIsLoading(true);
    setSynced(false);
    setLoadError(null);
    try {
      const data = await utils.client.admin.tumSync.getTumSyncUsers.query(
        { orgId: selectedOrgId, teamId: selectedTeamId ?? null },
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      if (!data.tumEnabled) return;

      setUsers(
        data.users.map((u) => ({
          axleProfileId: u.axleProfileId,
          givenName: u.givenName,
          familyName: u.familyName,
          currentIsCoach: u.currentIsCoach,
          newIsCoach: u.newIsCoach,
          currentHasMobileAccess: u.currentHasMobileAccess,
          newHasMobileAccess: u.newHasMobileAccess,
          currentHasWebAccess: u.currentHasWebAccess,
          newHasWebAccess: u.newHasWebAccess,
          currentTeamNames: u.currentTeamNames,
          newTeamNames: u.newTeamNames,
          currentCoachTeamNames: u.currentCoachTeamNames,
          newCoachTeamNames: u.newCoachTeamNames,
        })),
      );
      setCount(data.count);
      setHasLoaded(true);
    } catch (err) {
      if (timedOut) {
        setLoadError(
          'Loading users timed out. The request was cancelled — please try again.',
        );
      } else if (!controller.signal.aborted) {
        setLoadError(
          err instanceof Error
            ? err.message
            : 'Failed to load users. Check the server logs for details.',
        );
      }
    } finally {
      clearTimeout(timeoutId);
      if (loadAbortRef.current === controller) {
        loadAbortRef.current = null;
        setIsLoading(false);
      }
    }
  }, [selectedOrgId, selectedTeamId, utils]);

  const handleSync = useCallback(async () => {
    if (!selectedOrgId || selectedTeamId === undefined) return;
    setIsSyncing(true);
    setSyncedCount(0);
    setFailureCount(0);
    try {
      const result = await runTumSyncMutation.mutateAsync({
        orgId: selectedOrgId,
        teamId: selectedTeamId ?? null,
      });
      setSyncedCount(result.syncedCount);
      setFailureCount(result.failureCount);
      setSynced(true);
    } finally {
      setIsSyncing(false);
    }
  }, [selectedOrgId, selectedTeamId, runTumSyncMutation]);

  const tumEnabled = tumEnabledQuery.data?.tumEnabled ?? null;
  const isCheckingTumEnabled =
    !!selectedOrgId && tumEnabledQuery.isFetching && tumEnabled === null;

  return {
    selectedOrgId,
    selectedTeamId,
    users,
    tumEnabled,
    isCheckingTumEnabled,
    adminHasCoachProfile,
    isCheckingCoachProfile:
      !!selectedOrgId &&
      coachProfilesQuery.isFetching &&
      adminHasCoachProfile === null,
    count,
    hasLoaded,
    isLoading,
    isSyncing,
    synced,
    syncedCount,
    failureCount,
    loadError,
    orgsQuery,
    teamsQuery,
    handleOrgChange,
    handleTeamChange,
    handleLoadUsers,
    handleSync,
  };
}
